import {
  AXIS,
  CURSOR_MODE,
  DEFAULT_QUAILTY_ID,
  DIMENSION,
  MEASURE_UNIT_NAME_MAP,
  MOVE_MODE,
  QUALITY_ID_TO_QUALITY,
  ROTATE_MODE,
  TRANSFORM_SPACE_MODE,
  UNITY_2D_VIEW_MODE,
} from '../../../config/constant/unityConstants';

import { CURATOR_MENU_SECTION } from './curatorMenuPreferencesSlicer';
import { ROOM_TYPE } from '../../../constants/projectConstants';
import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

//import { captureException } from '@sentry/react';

const LS_TEMPORARY_PREVENT_BACKUP_POPUP = 'LF_TEMPORARY_PREVENT_BACKUP_POPUP';

export const SAVE_PROJECT_MODE = {
  SAVE: 'SAVE',
  NEW: 'NEW',
  CLOSE_CURATOR: 'CLOSE_CURATOR',
  SAVE_ON_LOCAL: 'SAVE_ON_LOCAL',
};

export const SETTINGS_SECTION = {
  RESOLUTION_PRESETS: 'RESOLUTION_PRESETS',
  CAMERA_SETTINGS: 'CAMERA_SETTINGS',
  GRAPHIC_QUALITY: 'GRAPHIC_QUALITY',
  DEFAULT_MEASURE_UNITS: 'DEFAULT_MEASURE_UNITS',
  METADATA_SETTINGS: 'METADATA_SETTINGS',
};

const getUuid = () => {
  let uuid = sessionStorage.getItem('CURATOR_UUID')
  if (uuid) {
    return uuid
  } else {
    const uuid = uuidv4();
    sessionStorage.setItem('CURATOR_UUID', uuid)
    return uuid;
  }
}

const getTempPreventBackupPopup = () => {
  try {
    const lsValue = localStorage.getItem(LS_TEMPORARY_PREVENT_BACKUP_POPUP);
    return JSON.parse(lsValue);
  } catch (e) {
    // captureException(e);
    return null;
  }
}

// Change old Project and Images data as per for visible in glass and visible in gloss material according to
// visible in reflection value
function updateOldProjectsLightData(lights) {
  return lights?.map((_light) => {
    let _value = false;

    if (_light?.visibleInReflection) _value = true;

    let light = { ..._light };

    light.visibleInGlass = _value;
    light.visibleInGlossyMaterials = _value;

    return light;
  });
}


export const initialState = {
  uuid: getUuid(), // unique tab uuid for curator
  calculatedRoomId: null,
  calculatedBaseRoomId: null,
  pageTitle: '',
  preventResetDuringUrlChange: false, // sometimes we want to prevent curator reload whole room when url change -> for example when we create project from room
  curatorInitialized: process.env.REACT_APP_CURATOR_INITIALIZED === 'true',
  sidebarSection: null, //CURATOR_MENU_SECTION.CAMERAS,
  sidebarExpanded: false,
  preventProjectInitialization: false,
  initialRoomJsonData: null,
  groupPropJsonData: null,
  groupPropInitialized: false,
  groupPropLoadedCompletely: false,
  libraryPropInstantiated: false,
  filterOpen: null, // 'product', 'texture', 'material',
  imageScanSearchOnboardingData: {
    isActive: false,
    steps: [],
    currentStep: 1,
    isCompleted: false,
  },
  imageScanSearchData: {
    show: false,
    open: false,
    file: null,
  },
  isV1UploadedProject: false,
  layoutData: {},
  roomData: {},
  propsData: [],
  unityData: {},
  initialUnityData: null,
  saved: true,
  saveProjectDialogOpen: false,
  projectData: [],
  unityTrigger: false,
  projectJson: {},
  settingsNotSaved: false,
  individualProjectData: {},
  unityLoaded: false,
  roomInitialized: false,
  allPropsLoaded: false,
  roomLoadedCompletely: false,
  curatorLoader: false,
  saveProjectMode: SAVE_PROJECT_MODE.SAVE,
  unity2DViewMode: UNITY_2D_VIEW_MODE.TOP,
  dimension: DIMENSION._3D,
  ligthEffectActive: true,
  cursorMode: CURSOR_MODE.MOVE,
  selectedAxis: AXIS.ALL,
  saveProjectLoading: false,
  transformSpaceMode: TRANSFORM_SPACE_MODE.GLOBAL,
  isGizmoPivotInCenter: false,
  // rotateMode: null, // ROTATE_MODE.GLOBAL
  settingData: {},
  oldSettingData: {},
  defaultDetaData: [],
  textureVersionSelectorDialogOpen: false,
  importProjectTexturesConflictList: [],
  openProjectTexturesVersionWasUpdated: [],
  settingsSection: null,
  objectSelectionActive: false, // changes to true when user needs to select an object -> for example object for camera focus or for metadata etc
  projectParsed: false,
  temporaryPreventBackupPopup: getTempPreventBackupPopup(), // when true backup popup will not be shown -> this is a case for opening templates from website for example https://app.clickup.com/t/3326989/DEV-11062 . The value is an object with room id and type room or other data
  onboardingProductMaterial: [],
  onboardingOtherMaterial: [],
  onboardingProducts: [],
  allOnboardingData: {},
  selectedOnboardingMaterial: {},
  selectedOnboardingProduct: {},
  selectedOnboardingOtherMaterial: {},
  requestVideoDialogOpen: false,
  createPropGroupDialog: false,
  isBooleanOperationProcessing: false,
  deletePropCompleted: false,
  clickedUpdateConfigurator: false,
  projectLoadedTexturesData: null,
  openAlignmentTool: false,
  guestSignupComplete: false,
  productNotReady: null,
  guestUserProductObject: null,
  savedMaterialPreset: false,
  canSaveProject: false,
  canSaveNewProject: false,
};

const curator = createSlice({
  name: 'curator',
  initialState,
  reducers: {
    resetState: (state) => {
      return {
        ...initialState,
        settingData: state.settingData,
        oldSettingData: state.oldSettingData,
      };
    },
    setCanSaveProject: (state, { payload }) => {
      state.canSaveProject = payload;
    },
    setCanSaveNewProject: (state, { payload }) => {
      state.canSaveNewProject = payload;
    },
    setPageTitle: (state, { payload }) => {
      state.pageTitle = payload;
    },
    toggleSidebarSection: (state, { payload }) => {
      state.sidebarSection = state.sidebarSection === payload ? null : payload;
    },
    setSidebarSection: (state, { payload }) => {
      state.sidebarSection = payload;
    },
    setLayoutData: (state, { payload }) => {
      state.layoutData = payload;
    },
    setRoomData: (state, { payload }) => {
      state.roomData = payload;
    },
    setPropsData: (state, { payload }) => {
      state.propsData = payload;
    },
    setUnityData: (state, { payload }) => {
      if (!payload?.hasNewLightInfo && payload?.roomLightData?.length) {
        payload.roomLightData = updateOldProjectsLightData(payload?.roomLightData);
      }
      state.unityData = payload;
    },
    setInitialUnityData: (state, { payload }) => {
      state.initialUnityData = payload;
    },
    setSaveProjectDialogOpen: (state, { payload }) => {
      state.saveProjectDialogOpen = payload;
    },
    setProjectData: (state, { payload }) => {
      state.projectData = payload;
    },
    setUnityTrigger: (state, { payload }) => {
      state.setUnityTrigger = payload;
    },
    setProjectJson: (state, { payload }) => {
      state.projectJson = payload;
    },
    setIndividualProjectData: (state, { payload }) => {
      state.individualProjectData = {
        ...payload,
        member_details: {
          ...payload.member_details,
          owner_organization: payload.owner_organization,
        },
      };
    },
    setUnityLoaded: (state, { payload }) => {
      state.unityLoaded = payload;
    },
    setRoomInitialized: (state, { payload }) => {
      state.roomInitialized = payload;
    },
    setAllPropsLoaded: (state, { payload }) => {
      state.allPropsLoaded = payload;
    },
    setRoomLoadedCompletely: (state, { payload }) => {
      state.roomLoadedCompletely = payload;
    },
    setCuratorLoader: (state, { payload }) => {
      state.curatorLoader = payload;
    },
    getCuratorSettingData: (state, { payload }) => {
      if (Array.isArray(payload?.json_data?.graphicQuality)) {
        // mutate for now
        payload.json_data.graphicQuality = payload.json_data.graphicQuality.map((item) => {
          // we want to ignore user configuration for now and open room always with default configuration https://app.clickup.com/t/3326989/DEV-1004
          return item.id === DEFAULT_QUAILTY_ID
            ? { ...item, isSelected: true }
            : { ...item, isSelected: false };
        });
      }

      state.oldSettingData = payload;
      state.settingData = payload;
    },
    setSaved: (state, { payload }) => {
      state.saved = payload;
    },
    setCuratorSettingData: (state, { payload }) => {
      state.settingData = payload;
    },
    setIsV1UploadedProject: (state, { payload }) => {
      state.isV1UploadedProject = payload;
    },
    setInitialRoomJsonData: (state, { payload }) => {
      state.initialRoomJsonData = payload;
    },
    setGroupPropJsonData: (state, { payload }) => {
      state.groupPropJsonData = payload;
    },
    setGroupPropInitialized: (state, { payload }) => {
      state.groupPropInitialized = payload;
    },
    setGroupPropLoadedCompletely: (state, { payload }) => {
      state.groupPropLoadedCompletely = payload;
    },
    setLibraryPropInstantiated: (state, { payload }) => {
      state.libraryPropInstantiated = payload;
    },
    updateCameraSettings: (state, { payload }) => {
      const { settingsKey, value } = payload;
      state.settingData = {
        ...state.settingData,
        json_data: {
          ...state.settingData?.json_data,
          cameraSetting: {
            ...state.settingData?.json_data?.cameraSetting,
            [settingsKey]: parseFloat(value),
          },
        },
      };
    },
    setSelectedGraphicQuality: (state, { payload }) => {
      // this action accepts id
      const { id } = payload;
      const updatedGraphicQuality = [...state.settingData.json_data.graphicQuality].map((item) =>
        item.id === id ? { ...item, isSelected: true } : { ...item, isSelected: false }
      );
      state.settingData.json_data.graphicQuality = updatedGraphicQuality;
    },
    setGraphicQuality: (state, { payload }) => {
      // hits method accepts graphic `QUAILTY`
      const { quality } = payload;
      const updatedGraphicQuality = [...state.settingData.json_data.graphicQuality].map((item) => {
        const itemQuality = QUALITY_ID_TO_QUALITY[item.id];
        return itemQuality === quality
          ? { ...item, isSelected: true }
          : { ...item, isSelected: false };
      });
      state.settingData.json_data.graphicQuality = updatedGraphicQuality;
    },
    setSelectedMeasureUnit: (state, { payload }) => {
      const { id } = payload;
      const updatedMeasureUnit = [...state.settingData.json_data.measureUnit].map((item) =>
        item.id === id ? { ...item, isSelected: true } : { ...item, isSelected: false }
      );
      state.settingData.json_data.measureUnit = updatedMeasureUnit;
      state.settingsNotSaved = true;

      state.oldSettingData.json_data.measureUnit = updatedMeasureUnit;
    },
    setMetadataIsRender: (state, { payload }) => {
      const { id, isRender } = payload;

      state.settingData.json_data.metaData = state.settingData.json_data.metaData.map((item) => {
        return id === item.id ? { ...item, is_render: isRender } : item;
      });
    },
    setSelectedResolutionPreset: (state, { payload }) => {
      state.settingData.json_data.resolutionPresent = [
        ...state.settingData.json_data.resolutionPresent,
      ].map((resolutionPreset) => {
        return { ...resolutionPreset, isSelected: resolutionPreset.id === payload };
      });
    },
    updateResolutionPreset: (state, { payload }) => {
      state.settingData.json_data.resolutionPresent =
        state.settingData.json_data.resolutionPresent.map((preset) => {
          return preset.id === payload.id ? { ...preset, ...payload } : preset;
        });
    },
    editCuratorSettingData: (state, { payload }) => {
      state.oldSettingData = { ...state.oldSettingData, ...payload };
      state.settingData = { ...state.settingData, ...payload };
    },
    setDefaultMetaData: (state, { payload }) => {
      state.defaultDetaData = payload;
    },
    toggleDimension: (state, { payload }) => {
      state.dimension = state.dimension === DIMENSION._2D ? DIMENSION._3D : DIMENSION._2D;
    },
    setUnity2DViewMode: (state, { payload }) => {
      state.unity2DViewMode = payload;
    },
    syncLightFromUnity: (state, { payload }) => {
      const exist = Boolean(state.unityData.roomLightData.find((l) => l.id === payload.id));
      if (exist) {
        state.unityData.roomLightData = state.unityData.roomLightData.map((l) => {
          return l.id === payload.id ? payload : l;
        });
      } else {
        state.unityData.roomLightData.unshift(payload);
      }
    },
    updateLight: (state, { payload }) => {
      state.unityData.roomLightData = state.unityData.roomLightData.map((light) => {
        return light.id === payload.id ? payload : light;
      });
    },
    partialUpdateLight: (state, { payload }) => {
      state.unityData.roomLightData = state.unityData.roomLightData.map((light) => {
        return light.id === payload.id ? { ...light, ...payload } : light;
      });
    },
    deleteLight: (state, { payload }) => {
      state.unityData.roomLightData = state.unityData.roomLightData.filter(
        (light) => light.id !== payload
      );
    },
    toggleSidebarExpanded: (state, { payload }) => {
      state.sidebarExpanded = typeof payload === 'boolean' ? payload : !state.sidebarExpanded;
    },
    setSidebarExpanded: (state, { payload }) => {
      state.sidebarExpanded = payload;
    },
    toggleFilterOpen: (state, { payload }) => {
      if (!payload) {
        state.filterOpen = null;
      }
      state.filterOpen = state.filterOpen === payload ? null : payload;
    },
    setImageScanSearchOnboardingData: (state, { payload }) => {
      state.imageScanSearchOnboardingData = {
        ...state.imageScanSearchOnboardingData,
        ...payload,
      };
    },
    setImageScanSearchFile: (state, { payload }) => {
      state.imageScanSearchData = {
        ...state.imageScanSearchData,
        file: payload,
        show: payload === null ? false : true,
      };
    },
    setImageScanSearchShow: (state, { payload }) => {
      state.imageScanSearchData = {
        ...state.imageScanSearchData,
        show: payload,
      };
    },
    toggleImageScanSearchOpen: (state, { payload }) => {
      state.imageScanSearchData = {
        ...state.imageScanSearchData,
        open: !state.imageScanSearchData.open,
      };

      if (
        state.imageScanSearchOnboardingData.isActive &&
        state.imageScanSearchOnboardingData.currentStep === 1 &&
        state.imageScanSearchData.open
      ) {
        state.imageScanSearchOnboardingData = {
          ...state.imageScanSearchOnboardingData,
          currentStep: state.imageScanSearchOnboardingData.currentStep + 1,
        };
      }
    },
    closeFilter: (state) => {
      state.filterOpen = null;
    },
    setCursorMode: (state, { payload }) => {
      state.cursorMode = payload;
    },
    setLightEffectActive: (state, { payload }) => {
      state.ligthEffectActive = payload;
    },
    setTransformSpaceMode: (state, { payload }) => {
      state.transformSpaceMode = payload;
    },
    setGizmoPivotToCenter: (state, { payload }) => {
      state.isGizmoPivotInCenter = payload;
    },
    toggleSelectedAxis: (state, { payload }) => {
      state.selectedAxis = state.selectedAxis === payload ? AXIS.ALL : payload;
    },
    resetCursorModeConfig: (state, { payload }) => {
      state.selectedAxis = AXIS.ALL;
      state.transformSpaceMode = TRANSFORM_SPACE_MODE.GLOBAL;
    },
    setCalculatedRoomId: (state, { payload }) => {
      state.calculatedRoomId = payload;
    },
    setCalculatedBaseRoomId: (state, { payload }) => {
      state.calculatedBaseRoomId = payload;
    },
    setSaveProjectMode: (state, { payload }) => {
      state.saveProjectMode = payload;
    },
    setSaveProjectLoading: (state, { payload }) => {
      state.saveProjectLoading = payload;
    },
    setCuratorInitialized: (state, { payload }) => {
      state.curatorInitialized = payload;
    },
    setRoomLightData: (state, { payload }) => {
      state.unityData.roomLightData = payload;
    },
    setTextureVersionSelectorDialogOpen: (state, { payload }) => {
      state.textureVersionSelectorDialogOpen = payload;
    },
    setImportProjectTexturesConflictList: (state, { payload }) => {
      state.importProjectTexturesConflictList = payload;
    },
    setOpenProjectTexturesVersionWasUpdated: (state, { payload }) => {
      state.openProjectTexturesVersionWasUpdated = payload;
    },
    setSettingsSection: (state, { payload }) => {
      state.settingsSection = payload;
    },
    toggleSettingsSection: (state, { payload }) => {
      state.settingsSection = payload && payload === state.settingsSection ? null : payload;
    },
    openSettingsSection: (state, { payload }) => {
      state.settingsSection = payload;
    },
    setSettingsSaved: (state, { payload }) => {
      state.settingsNotSaved = payload;
    },
    saveAsNew: (state, { payload }) => {
      const { project } = payload;
      state.preventProjectInitialization = true;
      state.individualProjectData = {
        ...project,
        member_details: {
          ...project.member_details,
          owner_organization: project.owner_organization,
        },
      };
      state.roomData = initialState.roomData;
    },
    unblockProjectInitialization: (state) => {
      state.preventProjectInitialization = false;
    },
    setObjectSelectionActive: (state, { payload }) => {
      state.objectSelectionActive = payload;
    },
    setProjectParsed: (state, { payload }) => {
      state.projectParsed = payload;
    },
    setPreventResetDuringUrlChange: (state, { payload }) => {
      state.preventResetDuringUrlChange = payload;
    },
    setTemporaryPreventBackupPopup: (state, { payload }) => {
      state.temporaryPreventBackupPopup = payload;
      localStorage.setItem(LS_TEMPORARY_PREVENT_BACKUP_POPUP, JSON.stringify(payload));
    },
    setFastPreview: (state, { payload }) => {
      state.fastPreview = payload;
    },
    setIsFirstFastPreview: (state, { payload }) => {
      state.isFirstFastPreview = payload;
    },
    setTabUUID: (state, { payload }) => {
      state.tabUUID = payload;
    },
    resetOnboardingStates: (state) => {
      state.onboardingProductMaterial = [];
      state.onboardingProducts = [];
      state.allOnboardingData = {};
      state.selectedOnboardingMaterial = {};
      state.selectedOnboardingProduct = {};
      state.selectedOnboardingOtherMaterial = {};
      state.onboardingOtherMaterial = {};
    },
    setOnboardingProducts: (state, { payload }) => {
      state.onboardingProducts = payload;
    },
    setOnboardingProductMaterial: (state, { payload }) => {
      state.onboardingProductMaterial = payload;
    },
    setOnboardingOtherMaterial: (state, { payload }) => {
      state.onboardingOtherMaterial = payload;
    },
    setAllOnboardingData: (state, { payload }) => {
      state.allOnboardingData = payload;
    },
    setSelectedOnboardingProduct: (state, { payload }) => {
      state.selectedOnboardingProduct = payload;
    },
    setSelectedOnboardingMaterial: (state, { payload }) => {
      state.selectedOnboardingMaterial = payload;
    },
    setSelectedOnboardingOtherMaterial: (state, { payload }) => {
      state.selectedOnboardingOtherMaterial = payload;
    },
    setRequestVideoDialogOpen: (state, { payload }) => {
      state.requestVideoDialogOpen = payload;
    },
    setCreatePropGroupDialog: (state, { payload }) => {
      state.createPropGroupDialog = payload;
    },
    setBooleanOperationProcessing: (state, { payload }) => {
      state.isBooleanOperationProcessing = payload;
    },
    setDeletePropCompleted: (state, { payload }) => {
      state.deletePropCompleted = payload;
    },
    setClickedUpdateConfigurator: (state, { payload }) => {
      state.clickedUpdateConfigurator = payload;
    },
    setProjectLoadedTexturesData: (state, { payload }) => {
      state.projectLoadedTexturesData = payload;
    },
    setOpenAlignmentTool: (state, { payload }) => {
      state.openAlignmentTool = payload;
    },
    setGuestSignupComplete: (state, { payload }) => {
      state.guestSignupComplete = payload;
    },
    setProductNotReady: (state, { payload }) => {
      state.productNotReady = payload;
    },
    setGuestUserProductObject: (state, { payload }) => {
      state.guestUserProductObject = payload;
    },
    setSavedMaterialPreset: (state, { payload }) => {
      state.savedMaterialPreset = payload;
    }
  },
});

export const {
  setCanSaveProject,
  setCanSaveNewProject,
  toggleSidebarSection,
  setCuratorLoader,
  setIndividualProjectData,
  setProjectJson,
  setUnityTrigger,
  setProjectData,
  setLayoutData,
  setRoomData,
  setPropsData,
  setUnityData,
  setInitialUnityData,
  setSettingsSaved,
  setCuratorSettingData,
  editCuratorSettingData,
  getCuratorSettingData,
  updateCameraSettings,
  setMetadataIsRender,
  setSelectedGraphicQuality,
  setGraphicQuality,
  setSelectedMeasureUnit,
  setDefaultMetaData,
  syncLightFromUnity,
  updateLight,
  partialUpdateLight,
  toggleSidebarExpanded,
  setSidebarExpanded,
  toggleFilterOpen,
  toggleImageScanSearchOpen,
  setImageScanSearchShow,
  setImageScanSearchFile,
  setImageScanSearchOnboardingData,
  toggleDimension,
  setUnity2DViewMode,
  setLightEffectActive,
  setCursorMode,
  setTransformSpaceMode,
  setGizmoPivotToCenter,
  setSelectedAxis,
  toggleSelectedAxis,
  setSaveProjectDialogOpen,
  setSaveProjectMode,
  setSaveProjectLoading,
  resetCursorModeConfig,
  setUnityLoaded,
  setRoomInitialized,
  setAllPropsLoaded,
  setRoomLoadedCompletely,
  setCuratorInitialized,
  setInitialRoomJsonData,
  setGroupPropJsonData,
  setGroupPropInitialized,
  setGroupPropLoadedCompletely,
  setLibraryPropInstantiated,
  setSidebarSection,
  setIsV1UploadedProject,
  setSelectedResolutionPreset,
  updateResolutionPreset,
  setRoomLightData,
  setTextureVersionSelectorDialogOpen,
  setImportProjectTexturesConflictList,
  setOpenProjectTexturesVersionWasUpdated,
  setSettingsSection,
  toggleSettingsSection,
  openSettingsSection,
  saveAsNew,
  unblockProjectInitialization,
  setCalculatedRoomId,
  setCalculatedBaseRoomId,
  setPageTitle,
  setObjectSelectionActive,
  setProjectParsed,
  setPreventResetDuringUrlChange,
  setTemporaryPreventBackupPopup,
  setFastPreview,
  setIsFirstFastPreview,
  setTabUUID,
  setOnboardingProducts,
  setOnboardingProductMaterial,
  setOnboardingOtherMaterial,
  setAllOnboardingData,
  setSelectedOnboardingProduct,
  setSelectedOnboardingMaterial,
  setSelectedOnboardingOtherMaterial,
  setRequestVideoDialogOpen,
  setCreatePropGroupDialog,
  setBooleanOperationProcessing,
  setDeletePropCompleted,
  setClickedUpdateConfigurator,
  setProjectLoadedTexturesData,
  setOpenAlignmentTool,
  setGuestSignupComplete,
  setProductNotReady,
  setGuestUserProductObject,
  setSavedMaterialPreset
} = curator.actions;
export const curatorActions = curator.actions;

export const curatorSelector = (state) => state.curator;
export const curatorReducer = curator.reducer;
export default curatorReducer;

export const selectCanSaveProject = (state) => curatorSelector(state).canSaveProject;
export const selectCanSaveNewProject = (state) => curatorSelector(state).canSaveNewProject;
export const selectSelectedOnboardingOtherMaterial = (state) =>
  curatorSelector(state).selectedOnboardingOtherMaterial;
export const selectSelectedOnboardingMaterial = (state) =>
  curatorSelector(state).selectedOnboardingMaterial;
export const selectSelectedOnboardingProduct = (state) =>
  curatorSelector(state).selectedOnboardingProduct;
export const selectAllOnboardingData = (state) => curatorSelector(state).allOnboardingData;
export const selectOnboardingProductMaterial = (state) =>
  curatorSelector(state).onboardingProductMaterial;
export const selectOnboardingOtherMaterial = (state) =>
  curatorSelector(state).onboardingOtherMaterial;
export const selectOnboardingProducts = (state) => curatorSelector(state).onboardingProducts;
export const selectUuid = (state) => curatorSelector(state).uuid;
export const selectTabUUID = (state) => curatorSelector(state).tabUUID;
export const selectIsLoading = (state) => !curatorSelector(state).curatorLoader;
export const selectUnityLoaded = (state) => curatorSelector(state).unityLoaded;
export const selectAllPropsLoaded = (state) => curatorSelector(state).allPropsLoaded;
export const selectCuratorInitFinished = (state) => {
  const { allPropsLoaded, roomLoadedCompletely, projectParsed } = curatorSelector(state);
  return allPropsLoaded && roomLoadedCompletely && projectParsed;
};
export const selectRoomLoadedCompletely = (state) => curatorSelector(state).roomLoadedCompletely;
export const selectUnityData = (state) => curatorSelector(state).unityData;
export const selectInitialUnityData = (state) => curatorSelector(state).initialUnityData;
export const selectDimension = (state) => curatorSelector(state).dimension;
export const selectIs2DMode = (state) => curatorSelector(state).dimension === DIMENSION._2D;
export const selectIs3DMode = (state) => curatorSelector(state).dimension === DIMENSION._3D;
export const selectCursorMode = (state) => curatorSelector(state).cursorMode;
export const selectIsStyleOnlyMode = (state) =>
  curatorSelector(state).cursorMode === CURSOR_MODE.STYLE_ONLY;
export const selectIsRotationMode = (state) => selectCursorMode(state) === CURSOR_MODE.ROTATE;
export const selectSelectedAxis = (state) => curatorSelector(state).selectedAxis;
export const selectIsOnlyOneAxisSelected = (state) =>
  curatorSelector(state).selectedAxis !== AXIS.ALL;
export const selectLightEffectActive = (state) => curatorSelector(state).ligthEffectActive;
export const selectTransformSpaceMode = (state) => curatorSelector(state).transformSpaceMode;
export const selectIsGizmoPivotInCenter = (state) => curatorSelector(state).isGizmoPivotInCenter;
export const selectUnity2DViewMode = (state) => curatorSelector(state).unity2DViewMode;
export const selectSaveProjectDialogOpen = (state) => curatorSelector(state).saveProjectDialogOpen;
export const selectSaveProjectMode = (state) => curatorSelector(state).saveProjectMode;
export const selectSaveProjectLoading = (state) => curatorSelector(state).saveProjectLoading;
export const selectSettingData = (state) => curatorSelector(state).settingData;
export const selectOldSettingData = (state) => curatorSelector(state).oldSettingData;
export const selectMeasureUnitList = (state) =>
  curatorSelector(state).oldSettingData?.json_data?.measureUnit;
export const selectSelectedMeasureUnits = (state) =>
  curatorSelector(state).oldSettingData?.json_data?.measureUnit?.find(
    (item) => item?.isSelected === true
  );
export const selectActiveMeasureUnitsName = (state) => {
  const selected = selectSelectedMeasureUnits(state);
  return MEASURE_UNIT_NAME_MAP[selected?.id];
};
export const selectMeasureUnitById = (state, id) => {
  const list = selectMeasureUnitList;
  return list.find((item) => item.id === id);
};
export const selectPreventResetDuringUrlChange = (state) =>
  state.curator.preventResetDuringUrlChange;
export const selectSettingsNotSaved = (state) => curatorSelector(state).settingsNotSaved;
export const selectResolutionPresets = (state) =>
  curatorSelector(state).settingData?.json_data?.resolutionPresent;
export const selectSidebarExpanded = (state) => state.curator.sidebarExpanded;

export const selectCuratorInitialized = (state) => curatorSelector(state).curatorInitialized;
export const curatorActionsDisabled = (state) => !curatorSelector(state).curatorInitialized;
export const selecteIsSidebarOpen = (state) => Boolean(state.curator.sidebarSection);
export const selectSidebarSection = (state) => state.curator.sidebarSection;
export const selecteIsFilterOpen = (state) => Boolean(state.curator.filterOpen);
export const selectedIsImageScanSearchOnboardingData = (state) =>
  state.curator.imageScanSearchOnboardingData;
export const selectedIsImageScanSearchOpen = (state) =>
  Boolean(state.curator.imageScanSearchData.open);
export const selectedIsImageScanSearchFile = (state) => state.curator.imageScanSearchData.file;
export const selectIsImageScanSearchFileShow = (state) => state.curator.imageScanSearchData.show;
export const selectRoomId = (state) => state.curator.roomData?.id;
export const selectRoomName = (state) => state.curator.roomData?.name;
export const selectMeasureUnit = (state) =>
  state.curator.settingData?.json_data?.measureUnit?.find((m) => m.isSelected);
export const selectIsSiloRoom = (state) =>
  state.curator.roomData?.room_type === ROOM_TYPE.SILO ||
  state.curator.individualProjectData?.project_type === ROOM_TYPE.SILO;
export const selectPreventProjectInitialization = (state) =>
  state.curator.preventProjectInitialization;
export const selectCalculatedRoomId = (state) => state.curator.calculatedRoomId; // state.curator.individualProjectData?.roomId || state.curator.roomData?.id
export const selectCalculatedBaseRoomId = (state) => state.curator.calculatedBaseRoomId; // state.curator.individualProjectData?.roomId || state.curator.roomData?.id
export const selectObjectSelectionActive = (state) => state.curator.objectSelectionActive;
export const selectInitialRoomJsonData = (state) => state.curator.initialRoomJsonData;
export const selectGroupPropJsonData = (state) => state.curator.groupPropJsonData;
export const selectGroupPropInitialized = (state) => state.curator.groupPropInitialized;
export const selectGroupPropLoadedCompletely = (state) => state.curator.groupPropLoadedCompletely;
export const selectLibraryPropInstantiated = (state) => state.curator.libraryPropInstantiated;

export const selectIndividualProjectData = (state) => state.curator.individualProjectData;
export const selectProjectJsonData = (state) => state.curator.projectJson;
export const selectProjectOwner = (state) => state.curator.individualProjectData?.member_details;
export const selectRoomData = (state) => state.curator.roomData;
export const selectProjectName = (state) => state.curator.individualProjectData?.name;
export const selectTextureVersionSelectorDialogOpen = (state) =>
  state.curator.textureVersionSelectorDialogOpen;
export const selectImportProjectTexturesConflictList = (state) =>
  state.curator.importProjectTexturesConflictList;
export const selectSettingsSection = (state) => state.curator.settingsSection;
export const selectProjectJsonUrl = (state) => selectIndividualProjectData(state)?.data?.json_file;
export const selectLightById = (state, id) => {
  return state.curator.unityData.roomLightData.find((light) => light.id === id);
};
export const selectOpenProjectTexturesVersionWasUpdated = (state) =>
  state.curator.openProjectTexturesVersionWasUpdated;

export const selectCuratorPageTitle = (state) => state.curator.pageTitle;
export const selectIsV1UploadedProject = (state) => state.curator.isV1UploadedProject;
export const selectTemporaryPreventBackupPopup = (state) =>
  state.curator.temporaryPreventBackupPopup;

export const selectProjectType = (state) => {
  const roomData = selectRoomData(state);
  const individualProjectData = selectIndividualProjectData(state);

  return individualProjectData?.project_type || roomData.room_type;
};

export const selectRoomInitialized = (state) => state.curator.roomInitialized;

export const selectCuratorSaved = (state) => state.curator.saved;
export const selectMetadataSettings = (state) => state.curator.settingData?.json_data?.metaData;
export const selectLightList = (state) => curatorSelector(state).unityData.roomLightData;
export const selectLightNames = (state) => {
  const lights = curatorSelector(state).unityData.roomLightData || [];
  return lights.map((l) => l.lightName);
};

export const selectAxisAvailability = (state) => {
  const is3D = selectIs3DMode(state);
  const unity2DViewMode = selectUnity2DViewMode(state);
  const isTopView = unity2DViewMode === UNITY_2D_VIEW_MODE.TOP;
  const isBackView = unity2DViewMode === UNITY_2D_VIEW_MODE.BACK;
  const isFrontView = unity2DViewMode === UNITY_2D_VIEW_MODE.FRONT;
  const isLeftView = unity2DViewMode === UNITY_2D_VIEW_MODE.LEFT;
  const isRightView = unity2DViewMode === UNITY_2D_VIEW_MODE.RIGHT;
  const cursorMode = selectCursorMode(state);

  const isAxisVisible = (axis) => {
    if (is3D) return true;
    if (cursorMode === CURSOR_MODE.ROTATE) return true;

    switch (axis) {
      case AXIS.X:
        return isTopView || isBackView || isFrontView;
      case AXIS.Y:
        return !isTopView;
      case AXIS.Z:
        return isTopView || isLeftView || isRightView;
      default:
        throw new Error('Not supported axis: ' + axis);
    }
  };

  return {
    [AXIS.X]: isAxisVisible(AXIS.X),
    [AXIS.Y]: isAxisVisible(AXIS.Y),
    [AXIS.Z]: isAxisVisible(AXIS.Z),
  };
};

export const selectIsRequestVideoDialogOpen = (state) => state.curator.requestVideoDialogOpen;
export const selectCreatePropGroupDialog = (state) => state.curator.createPropGroupDialog;
export const selectFastPreview = (state) => state.curator.fastPreview;
export const selectIsFirstFastPreview = (state) => state.curator.isFirstFastPreview;

export const selectIsBooleanOperationProcessing = (state) =>
  state.curator.isBooleanOperationProcessing;
export const selectDeletePropCompleted = (state) => state.curator.deletePropCompleted;
export const selectClickedUpdateConfigurator = (state) => state.curator.clickedUpdateConfigurator;
export const selectProjectLoadedTexture = (state) => state.curator.projectLoadedTexturesData;
export const selectOpenAlignmentTool = (state) => state.curator.openAlignmentTool;
export const selectGuestSignupComplete = (state) => state.curator.guestSignupComplete;

export const selectProductNotReady = (state) => state.curator.productNotReady;
export const selectGuestUserProductObject = (state) => state.curator.guestUserProductObject;
export const selectSavedMaterialPreset = (state) => state.curator.savedMaterialPreset;